import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { SEMANTIC_TAG } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../theming/useTheme';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { MandatoryAdditionalStyle, AdditionalStyleSheet } from '../components.utils';
import {
  getFontSizesBySemanticTag,
  getStyleBySize,
  SemanticTagThemeProps,
} from '../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../theming/fontSizeTranslator';

const componentConfigurationKey = 'articleParagraph';

interface ParagraphComponentDataProps {
  html: string;
  dataId?: string;
}

interface ParagraphComponentThemeProps extends SemanticTagThemeProps {
  linkColor: string;
  baseUnit: BaseUnit;
}

type ParagraphComponentProps = ParagraphComponentDataProps & MandatoryAdditionalStyle;

const getStyle = (theme: ParagraphComponentThemeProps) => {
  const { linkColor, baseUnit } = theme;
  return (element: string) => {
    switch (element) {
      case 'a': return StyleSheet.create({
        style: {
          color: linkColor,
          textDecoration: 'none',
        },
      }).style;

      case 'ol':
      case 'ul': return StyleSheet.create({
        style: {
          listStylePosition: 'inside',
          padding: 0,
          margin: `${baseUnit}px 0`,
        },
      }).style;

      default: return null;
    }
  };
};

const applyCssClasses = (html: string, theme: ParagraphComponentThemeProps): string => {
  const style = getStyle(theme);
  const typographyStyleP = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('p'));
  const typographyStyleH2 = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('h2'));
  const typographyStyleH3 = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('h3'));
  return html
    .replace(/<p/g, `<p class="${css(typographyStyleP)}"`)
    .replace(/<h2/g, `<p class="${css(typographyStyleH2)}"`)
    .replace(/<h3/g, `<p class="${css(typographyStyleH3)}"`)
    .replace(/<ol/g, `<ol class="${css(typographyStyleP, style('ol'))}"`)
    .replace(/<ul/g, `<ul class="${css(typographyStyleP, style('ul'))}"`)
    .replace(/<a/g, `<a class="${css(typographyStyleP, style('a'))}"`);
};

const extractWrapper = (html: string) => {
  const regex = /^<(\w+).*?>/;
  const match = html.match(regex);
  if (!match) {
    return ['p', html];
  }

  const indexOfLastElement = html.lastIndexOf('<');
  return [
    match[1],
    html.substr(0, indexOfLastElement).replace(match[0], ''),
  ];
};

const getWrapperAsElement = (wrapper: string, content: string, style: { [key: string]: any } | null, additionalStyle: AdditionalStyleSheet, dataId: string | undefined, fontSizesStyles: TranslatedFontSize) => {
  const typographyStyle = getStyleBySize(fontSizesStyles, getFontSizesBySemanticTag(wrapper as SEMANTIC_TAG));
  return React.createElement(
    wrapper,
    {
      dangerouslySetInnerHTML: { __html: content },
      className: css(typographyStyle, style, additionalStyle.style),
      'data-id': dataId,
    },
  );
};

export const Paragraph: React.FunctionComponent<ParagraphComponentProps> = props => {
  const { html, additionalStyle, dataId } = props;
  const themeProps = useTheme<ParagraphComponentThemeProps>(componentConfigurationKey);
  const { fontSizesStyles } = themeProps;
  const [wrapper, content] = extractWrapper(html);
  const wrapperStyle = getStyle(themeProps)(wrapper);

  return (
    <ErrorBoundary>
      {getWrapperAsElement(wrapper, applyCssClasses(content, themeProps), wrapperStyle, additionalStyle, dataId, fontSizesStyles)}
    </ErrorBoundary>
  );
};
